import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/** Провайдер моделей по meshId (клон предзагруженной glTF-детали). */
export interface ModelProvider {
  getModel(meshId: string): THREE.Object3D | null;
}

interface SystemsCatalog {
  meshes?: Record<string, string>;
}

/**
 * Статический источник контента (ADR-0005): каталог + glTF-модели из `public/content`
 * (в проде — сервер/CDN). Модели грузятся в рантайме — добавление/замена без
 * пересборки приложения. Draco/KTX2 подключим при необходимости.
 */
export class StaticContentSource {
  constructor(private readonly baseUrl = 'content/') {}

  /** Загрузить каталог и glTF-модели; вернуть провайдер (клоны по meshId). */
  async loadModels(): Promise<ModelProvider> {
    const catalog = (await fetch(`${this.baseUrl}catalog/systems.json`).then((r) => {
      if (!r.ok) throw new Error(`catalog HTTP ${r.status}`);
      return r.json();
    })) as SystemsCatalog;

    const meshes = catalog.meshes ?? {};
    const loader = new GLTFLoader();
    const urlCache = new Map<string, Promise<THREE.Object3D>>();

    const loadUrl = (url: string): Promise<THREE.Object3D> => {
      let p = urlCache.get(url);
      if (!p) {
        p = loader.loadAsync(`${this.baseUrl}${url}`).then((g) => g.scene);
        urlCache.set(url, p);
      }
      return p;
    };

    const byMesh = new Map<string, THREE.Object3D>();
    await Promise.all(
      Object.entries(meshes).map(async ([meshId, url]) => {
        byMesh.set(meshId, await loadUrl(url));
      }),
    );

    return {
      getModel: (meshId) => byMesh.get(meshId)?.clone(true) ?? null,
    };
  }
}
